import React, { useState, useEffect } from "react";
import {
  DatePicker,
  Button,
  Input,
  InputNumber,
  Select,
  Table,
  Space,
  Popconfirm,
  Form,
  Typography,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  deleteSpecificRoom,
  checkRoomAvailability,
  updateReservation,
} from "../../Store/Apis/ReservationApi";
import { useServiceProductsQuery } from "../../Store/Apis/servicesApi";

const { TextArea } = Input;
const { Option } = Select;

const EditQuotationForm = ({
  orderData,
  onSubmit,
  onCancel,
  companyID,
  onItemDeleted,
}) => {
  const [form] = Form.useForm();
  const [serviceDetails, setServiceDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [hasDeletedItems, setHasDeletedItems] = useState(false);
  const [hasNewItems, setHasNewItems] = useState(false);

  // --- Accommodation Products (mirrors BookNowModal logic) ---
  // BookNowModal calls: getProductOrServiceDetails("Accommodation", outletID, companyID)
  // The RTK query endpoint expects params matching backend query string.
  const outletID =
    orderData?.outletID ||
    orderData?.outletId ||
    orderData?.outletIDRef ||
    null;
  const resolvedCustomerID =
    orderData?.customerID || orderData?.customerId || null;
  const serviceProductsParams = {
    keyname: "Accommodation",
    outletID: outletID,
    ...(companyID ? { companyID } : {}), // only include if provided
    ...(!companyID && resolvedCustomerID
      ? { customerID: resolvedCustomerID }
      : {}),
  };
  const {
    data: accommodationData,
    isLoading: loadingProducts,
    isError: productsError,
  } = useServiceProductsQuery(
    serviceProductsParams,
    { skip: !outletID } // avoid firing until we have an outletID
  );

  // Flatten nested parent -> childRecords just like BookNowModal transformation.
  const flattenedAccommodation = (accommodationData?.data || []).flatMap(
    (parent) =>
      (parent?.childRecords || []).map((child) => ({
        parentId: parent.parentId,
        productServiceName: parent.productServiceName,
        serviceTypeName: parent.serviceTypeName,
        serviceCategoryName: parent.serviceCategoryName,
        typeName: child.typeName?.trim(),
        childId: child.childId,
        typeId: child.typeId,
        rate: Number(child.rate) || 0,
        code: child.code,
      }))
  );

  const accommodationOptions = flattenedAccommodation.map((item) => ({
    value: item.typeName, // use room package name as the selection value
    label: item.typeName,
    rate: item.rate,
    parentId: item.parentId,
    childId: item.childId,
    typeId: item.typeId,
    code: item.code,
    productServiceName: item.productServiceName,
  }));

  // Template values for adding new guests
  const [templateGuest, setTemplateGuest] = useState({
    guestName: "",
    guestEmail: "",
    checkInDate: dayjs(),
    checkOutDate: dayjs().add(1, "day"),
    roomType: undefined, // changed from "" so placeholder can appear
    rate: 0,
  });

  // When template room type changes, auto-populate its rate + IDs from fetched options
  useEffect(() => {
    if (!templateGuest.roomType) return;
    const match = accommodationOptions.find(
      (o) => o.label === templateGuest.roomType
    );
    if (match && match.rate !== templateGuest.rate) {
      setTemplateGuest((t) => ({ ...t, rate: match.rate }));
    }
  }, [templateGuest.roomType, accommodationOptions]);

  useEffect(() => {
    if (!orderData) return;
    // Prefer original property, but fall back to alternative arrays the API / submission might provide.
    const rawServices =
      orderData.serviceTypeAndName ||
      orderData.services ||
      orderData.serviceDetails ||
      orderData.serviceDetailList ||
      [];

    const formattedServices = rawServices.map((service, index) => {
      // Prefer explicit rate fields; derive from detail/total if absent.
      let rate =
        service.rate ||
        service.serviceRate ||
        service.detailRate ||
        service.unitRate ||
        service.servicePrice || // backend provided field
        0;
      let quantity =
        service.quantity ||
        service.serviceQuantity ||
        service.qty ||
        service.nights ||
        service.days ||
        1;
      // If we have a total / detailPrice but no rate, attempt derivation.
      const possibleTotal =
        service.total ||
        service.detailPrice ||
        service.lineTotal ||
        service.detailAmount ||
        service.servicePrice || // treat servicePrice as total if no explicit total
        null;
      if ((!rate || rate === 0) && possibleTotal && quantity) {
        rate = Number(possibleTotal) / Number(quantity);
      }
      if (!rate || isNaN(rate)) rate = 0;
      if (!quantity || isNaN(quantity)) quantity = 1;
      const checkIn =
        service.subCustomerCheckIn ||
        service.checkInDate ||
        service.customerCheckIn ||
        service.checkIn ||
        orderData?.orderDate ||
        null;
      const checkOut =
        service.subCustomerCheckOut ||
        service.checkOutDate ||
        service.customerCheckOut ||
        service.checkOut ||
        orderData?.checkOutDate ||
        null;
      // Derive days from dates if present; override quantity for accommodation-style lines
      let days = 1;
      if (checkIn && checkOut) {
        const dIn = dayjs(checkIn);
        const dOut = dayjs(checkOut);
        const diff = dOut.diff(dIn, "days");
        days = diff > 0 ? diff : 1;
      }
      // Use max between existing quantity and days to avoid losing nights multiplier
      const effectiveQuantity = Math.max(Number(quantity) || 1, days);
      // Simple per-line total: rate * days (no guest multiplier / aggregation)
      const lineTotal = rate * days;
      return {
        key: index,
        id:
          service.serviceID ||
          service.id ||
          service.serviceId ||
          service.childId,
        parentID: service.parentID || service.parentId || 0,
        serviceID:
          service.childID ||
          service.childId ||
          service.serviceID ||
          service.serviceId ||
          service.id,
        // include common backend variants, prefer explicit serviceTypeId
        typeID: service.typeID || service.typeId || service.serviceTypeId,
        keyID:
          service.keyID ||
          service.keyId ||
          service.roomId ||
          service.roomID ||
          null,
        serviceName:
          service.serviceName ||
          service.name ||
          service.productServiceName ||
          service.roomType ||
          "Service",
        productServiceName:
          service.productServiceName || service.productName || "",
        serviceCode: service.serviceCode || service.code || "",
        rate: Number(rate),
        quantity: effectiveQuantity,
        checkInDate: checkIn ? dayjs(checkIn) : dayjs(),
        checkOutDate: checkOut ? dayjs(checkOut) : dayjs().add(1, "day"),
        total: lineTotal,
        subCustomerName:
          service.subCustomerName ||
          service.guestName ||
          service.customerName ||
          "",
        subCustomerEmail:
          service.subCustomerEmail ||
          service.guestEmail ||
          service.customerEmail ||
          "",
        subCustomerID:
          service.subCustomerID ||
          service.guestID ||
          service.subCustomerId ||
          service.customerID ||
          service.customerId ||
          null,
        roomNumber: service.roomNumber || service.roomNo || "",
      };
    });

    setServiceDetails(formattedServices);

    form.setFieldsValue({
      customerName: orderData.customerName,
      phoneNumber: orderData.customerNumber,
      email: orderData.email,
      orderDate: orderData.orderDate ? dayjs(orderData.orderDate) : dayjs(),
      checkOutDate: orderData.checkOutDate
        ? dayjs(orderData.checkOutDate)
        : dayjs().add(1, "day"),
      notes: orderData.notes,
      discount: orderData.discount,
      discountType: orderData.discountType,
      gstNo: orderData.gstNo,
    });
  }, [orderData, form]);

  const columns = [
    {
      title: "#",
      dataIndex: "key",
      key: "index",
      width: 50,
      render: (text, record, index) => (
        <div
          style={{
            background: "linear-gradient(135deg, #043f6e 0%, #0a5a9b 100%)",
            color: "white",
            width: 24,
            height: 24,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {index + 1}
        </div>
      ),
    },
    {
      title: "Service/Room",
      dataIndex: "serviceName",
      key: "serviceName",
      width: 200,
      render: (text, record, index) => (
        <div>
          {companyID ? (
            <div>
              <Select
                value={record.serviceName || undefined} // ensure "" does not suppress placeholder
                onChange={(value) =>
                  handleServiceChange(index, "serviceName", value)
                }
                style={{
                  width: "100%",
                  borderRadius: 6,
                  border: "1.5px solid #e8f4f8",
                  marginBottom: 4,
                }}
                placeholder="Select Room Type"
                size="small"
                allowClear
              >
                {loadingProducts && (
                  <Option value="loading" disabled>
                    Loading...
                  </Option>
                )}
                {!loadingProducts &&
                  !productsError &&
                  accommodationOptions.length > 0 &&
                  accommodationOptions.map((opt) => (
                    <Option key={opt.value} value={opt.label}>
                      {opt.label}
                    </Option>
                  ))}
                {/* Hardcoded fallback room options removed */}
              </Select>
              <Input
                value={record.roomNumber}
                onChange={(e) =>
                  handleServiceChange(index, "roomNumber", e.target.value)
                }
                placeholder="Room Number"
                size="small"
                style={{
                  borderRadius: 6,
                  border: "1.5px solid #e8f4f8",
                  fontSize: 12,
                }}
              />
            </div>
          ) : (
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a1a" }}>
                {record.serviceName}
              </div>
              <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                {record.productServiceName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#999",
                  background: "#f8f9fa",
                  padding: "1px 4px",
                  borderRadius: 3,
                  display: "inline-block",
                  marginTop: 2,
                }}
              >
                {record.serviceCode}
              </div>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Guest Name",
      dataIndex: "subCustomerName",
      key: "subCustomerName",
      width: 150,
      render: (text, record, index) =>
        companyID ? (
          <Input
            value={text}
            onChange={(e) =>
              handleServiceChange(index, "subCustomerName", e.target.value)
            }
            placeholder="Enter guest name"
            size="small"
            style={{
              borderRadius: 6,
              border: "1.5px solid #e8f4f8",
            }}
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#1a1a1a",
            }}
          >
            {text || "N/A"}
          </div>
        ),
    },
    {
      title: "Guest Email",
      dataIndex: "subCustomerEmail",
      key: "subCustomerEmail",
      width: 180,
      render: (text, record, index) =>
        companyID ? (
          <Input
            value={text}
            onChange={(e) =>
              handleServiceChange(index, "subCustomerEmail", e.target.value)
            }
            placeholder="Enter guest email"
            size="small"
            type="email"
            style={{
              borderRadius: 6,
              border: "1.5px solid #e8f4f8",
            }}
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: "#1a1a1a",
            }}
          >
            {text || "N/A"}
          </div>
        ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      key: "rate",
      width: 100,
      render: (text, record, index) =>
        companyID ? (
          <InputNumber
            value={text}
            onChange={(value) => handleServiceChange(index, "rate", value)}
            min={0}
            prefix="R"
            size="small"
            style={{
              width: "100%",
              borderRadius: 6,
              border: "1.5px solid #e8f4f8",
            }}
            formatter={(value) =>
              `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
            }
            disabled
          />
        ) : (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#d4af37",
              background: "#fffbf0",
              padding: "4px 8px",
              borderRadius: 4,
              border: "1px solid #f0e68c",
              textAlign: "center",
            }}
          >
            R {text?.toLocaleString("en-ZA") || 0}
          </div>
        ),
    },
    {
      title: "Check In",
      dataIndex: "checkInDate",
      key: "checkInDate",
      width: 140,
      render: (text, record, index) => (
        <DatePicker
          value={text}
          onChange={(date) => handleDateChange(index, "checkInDate", date)}
          size="small"
          style={{
            width: "100%",
            borderRadius: 6,
            border: "1.5px solid #e8f4f8",
          }}
          format="YYYY-MM-DD"
        />
      ),
    },
    {
      title: "Check Out",
      dataIndex: "checkOutDate",
      key: "checkOutDate",
      width: 140,
      render: (text, record, index) => (
        <DatePicker
          value={text}
          onChange={(date) => handleDateChange(index, "checkOutDate", date)}
          size="small"
          style={{
            width: "100%",
            borderRadius: 6,
            border: "1.5px solid #e8f4f8",
          }}
          format="YYYY-MM-DD"
        />
      ),
    },
    {
      title: "Days",
      dataIndex: "days",
      key: "days",
      width: 60,
      render: (text, record) => {
        const days =
          record.checkOutDate && record.checkInDate
            ? record.checkOutDate.diff(record.checkInDate, "days") || 1
            : 1;
        return (
          <div
            style={{
              background: "linear-gradient(135deg, #043f6e 0%, #0a5a9b 100%)",
              color: "white",
              padding: "3px 6px",
              borderRadius: 4,
              textAlign: "center",
              fontSize: 11,
              fontWeight: 600,
            }}
          >
            {days}
          </div>
        );
      },
    },
    {
      title: "Total",
      dataIndex: "total",
      key: "total",
      width: 100,
      render: (text, record) => {
        const days =
          record.checkOutDate && record.checkInDate
            ? record.checkOutDate.diff(record.checkInDate, "days") || 1
            : 1;
        // Row total now always = rate * days (set during mapping / edits)
        const total = (record.rate || 0) * days;
        return (
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#d4af37",
              background: "#fffbf0",
              padding: "4px 8px",
              borderRadius: 4,
              border: "1px solid #f0e68c",
            }}
          >
            R {total.toLocaleString("en-ZA", { minimumFractionDigits: 2 })}
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      render: (text, record, index) => (
        <Space>
          <Popconfirm
            title={
              (record.keyID || record.id) && orderData?.orderId
                ? "Are you sure you want to delete this room/service from the system?"
                : "Are you sure you want to remove this service?"
            }
            description={
              (record.keyID || record.id) && orderData?.orderId
                ? "This will permanently delete the room assignment."
                : "This will remove the service from your current editing session."
            }
            onConfirm={() => handleRemoveService(index)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{
              style: {
                background: "#ff6b6b",
                borderColor: "#ff6b6b",
              },
            }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
              loading={deletingIndex === index}
              disabled={deletingIndex !== null}
              style={{
                borderRadius: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleServiceChange = (index, field, value) => {
    const updatedServices = [...serviceDetails];
    const prev = updatedServices[index] || {};
    let next = { ...prev, [field]: value };

    // When room/service is changed for company bookings, hydrate IDs from options
    if (field === "serviceName" && companyID) {
      const match = accommodationOptions.find((o) => o.label === value);
      if (match) {
        next = {
          ...next,
          parentID: match.parentId,
          serviceID: match.childId,
          typeID: match.typeId,
          productServiceName:
            match.productServiceName || next.productServiceName,
          serviceCode: match.code || next.serviceCode,
          rate: typeof match.rate === "number" ? match.rate : next.rate,
        };
      }
    }

    updatedServices[index] = next;

    // Recalculate total when rate or dates change
    if (
      field === "rate" ||
      field === "checkInDate" ||
      field === "checkOutDate" ||
      field === "serviceName"
    ) {
      const record = updatedServices[index];
      const days =
        record.checkOutDate && record.checkInDate
          ? record.checkOutDate.diff(record.checkInDate, "days") || 1
          : 1;
      updatedServices[index].total = (record.rate || 0) * days;
      updatedServices[index].quantity = days;
    }

    setServiceDetails(updatedServices);
  };

  // New: availability-aware date change for existing service lines
  const handleDateChange = async (index, field, date) => {
    const prevRecord = serviceDetails[index];
    const prevCheckIn = prevRecord.checkInDate;
    const prevCheckOut = prevRecord.checkOutDate;

    // Perform optimistic local update
    handleServiceChange(index, field, date);

    const workingRecord = {
      ...prevRecord,
      [field]: date,
      checkInDate: field === "checkInDate" ? date : prevRecord.checkInDate,
      checkOutDate: field === "checkOutDate" ? date : prevRecord.checkOutDate,
    };

    if (!workingRecord.checkInDate || !workingRecord.checkOutDate || !outletID)
      return; // need both dates & outlet
    try {
      const data = await checkRoomAvailability(
        workingRecord.checkInDate.format("YYYY-MM-DD"),
        workingRecord.checkOutDate.format("YYYY-MM-DD"),
        outletID
      );
      if (!data || data.availableRoomsCount < 1) {
        // revert
        const reverted = [...serviceDetails];
        reverted[index] = {
          ...reverted[index],
          checkInDate: prevCheckIn,
          checkOutDate: prevCheckOut,
        };
        const days =
          prevCheckOut && prevCheckIn
            ? prevCheckOut.diff(prevCheckIn, "days") || 1
            : 1;
        reverted[index].total = (reverted[index].rate || 0) * days;
        reverted[index].quantity = days;
        setServiceDetails(reverted);
        message.error("No rooms available for selected dates at this outlet.");
      } else {
        message.success("Dates available", 2);
      }
    } catch (e) {
      console.error("Availability check failed", e);
      message.error("Failed to verify availability");
    }
  };

  // New: availability-aware date change for the template guest before adding
  const handleTemplateDateChange = async (field, date) => {
    const prevTemplate = { ...templateGuest };
    const newTemplate = { ...templateGuest, [field]: date };
    setTemplateGuest(newTemplate);
    if (!newTemplate.checkInDate || !newTemplate.checkOutDate || !outletID)
      return;
    try {
      const data = await checkRoomAvailability(
        newTemplate.checkInDate.format("YYYY-MM-DD"),
        newTemplate.checkOutDate.format("YYYY-MM-DD"),
        outletID
      );
      if (!data || data.availableRoomsCount < 1) {
        setTemplateGuest(prevTemplate); // revert
        message.error(
          "No rooms available for those dates. Pick a different range."
        );
      } else {
        message.success("Dates available", 2);
      }
    } catch (e) {
      console.error("Template availability check failed", e);
      message.error("Failed to check availability");
    }
  };

  const handleRemoveService = async (index) => {
    const serviceToDelete = serviceDetails[index];

    console.log("Attempting to remove service:", serviceToDelete);

    // Extract possible identifiers (backend sometimes uses different casings)
    const resolvedOrderID =
      orderData?.orderId || orderData?.orderID || orderData?.OrderID || null;
    const resolvedKeyID =
      serviceToDelete.keyID ||
      serviceToDelete.keyId ||
      serviceToDelete.KeyID ||
      serviceToDelete.roomId ||
      serviceToDelete.roomID ||
      serviceToDelete.id ||
      null;
    const resolvedSubCustomerID =
      serviceToDelete.subCustomerID ||
      serviceToDelete.subCustomerId ||
      serviceToDelete.guestID ||
      serviceToDelete.customerID ||
      null;

    console.log("Resolved IDs for deletion:", {
      resolvedKeyID,
      resolvedSubCustomerID,
      resolvedOrderID,
    });

    // If this is an existing service with an ID, call the API
    if (resolvedKeyID && resolvedOrderID && resolvedSubCustomerID) {
      try {
        setDeletingIndex(index);
        const result = await deleteSpecificRoom(
          resolvedKeyID,
          resolvedSubCustomerID,
          resolvedOrderID
        );

        console.log("API response:", result);
        console.log("API response type:", typeof result);
        console.log("API response status:", result?.status);
        console.log("API response data:", result?.data);

        // Check for successful deletion
        if (result && result.success) {
          message.success("Room/Service deleted successfully");
          // Remove from local state after successful API call
          const updatedServices = serviceDetails.filter((_, i) => i !== index);
          setServiceDetails(updatedServices);
          setHasDeletedItems(true); // Mark that we've deleted items

          // Notify parent component that an item was deleted
          if (onItemDeleted) {
            onItemDeleted();
          }
        } else {
          console.error("API deletion failed:", result);
          // Try to get error message from different possible locations
          const errorMessage =
            result?.data?.message ||
            result?.error ||
            result?.data?.error ||
            "Failed to delete room/service";
          message.error(errorMessage);
        }
      } catch (error) {
        console.error("Error deleting service:", error);
        message.error(
          `Error deleting room/service: ${error.message || "Unknown error"}`
        );
      } finally {
        setDeletingIndex(null);
      }
    } else {
      console.warn("Not calling delete API: missing one or more IDs", {
        resolvedKeyID,
        resolvedSubCustomerID,
        resolvedOrderID,
        originalService: serviceToDelete,
      });
      message.warning(
        `Cannot delete on server. Missing: ${!resolvedKeyID ? "keyID " : ""}${
          !resolvedSubCustomerID ? "subCustomerID " : ""
        }$${!resolvedOrderID ? "orderID" : ""} (removed locally only)`
      );
      // For new services not yet saved, just remove from local state
      console.log("Removing new service from local state");
      const updatedServices = serviceDetails.filter((_, i) => i !== index);
      setServiceDetails(updatedServices);

      // Check if there are any remaining new items
      const remainingNewItems = updatedServices.filter(
        (service) => !service.keyID
      );
      if (remainingNewItems.length === 0) {
        setHasNewItems(false);
      }

      message.success("Service removed from editing session");
      if (onItemDeleted) {
        onItemDeleted({ localOnly: true });
      }
    }
  };

  const handleAddNewPerson = () => {
    // Try to derive IDs from selected template room type via accommodationOptions
    const match = templateGuest.roomType
      ? accommodationOptions.find((o) => o.label === templateGuest.roomType)
      : null;
    const derivedParentId =
      match?.parentId ?? serviceDetails[0]?.parentID ?? null;
    const derivedChildId =
      match?.childId ?? serviceDetails[0]?.serviceID ?? null;
    const derivedTypeId = match?.typeId ?? serviceDetails[0]?.typeID ?? null;
    const derivedRate =
      typeof match?.rate === "number" ? match.rate : templateGuest.rate || 0;
    const derivedProductServiceName =
      match?.productServiceName ??
      serviceDetails[0]?.productServiceName ??
      "ACCOMMODATION";
    const derivedServiceCode =
      match?.code ?? serviceDetails[0]?.serviceCode ?? "";

    const newService = {
      key: Date.now(),
      id: null,
      parentID: derivedParentId,
      serviceID: derivedChildId,
      typeID: derivedTypeId,
      serviceName: templateGuest.roomType,
      productServiceName: derivedProductServiceName,
      serviceCode: derivedServiceCode,
      rate: derivedRate,
      quantity:
        templateGuest.checkOutDate && templateGuest.checkInDate
          ? templateGuest.checkOutDate.diff(
              templateGuest.checkInDate,
              "days"
            ) || 1
          : 1,
      checkInDate: templateGuest.checkInDate,
      checkOutDate: templateGuest.checkOutDate,
      total:
        (derivedRate || 0) *
        (templateGuest.checkOutDate && templateGuest.checkInDate
          ? templateGuest.checkOutDate.diff(
              templateGuest.checkInDate,
              "days"
            ) || 1
          : 1),
      subCustomerName: templateGuest.guestName,
      subCustomerEmail: templateGuest.guestEmail,
      roomNumber: "",
    };

    setServiceDetails([...serviceDetails, newService]);
    setHasNewItems(true); // Mark that we have new items to save

    // Clear template after adding
    setTemplateGuest({
      guestName: "",
      guestEmail: "",
      checkInDate: dayjs(),
      checkOutDate: dayjs().add(1, "day"),
      roomType: undefined, // was ""
      rate: 0,
    });
  };

  const calculateSubtotal = () => {
    return serviceDetails.reduce(
      (sum, service) => sum + (service.total || 0),
      0
    );
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      // Only submit if there are new items added
      if (!hasNewItems) {
        message.info("No new items to save");
        setLoading(false);
        return;
      }

      // Only include new items (items without keyID)
      const newServices = serviceDetails.filter((service) => !service.keyID);

      if (newServices.length === 0) {
        message.info("No new items to save");
        setLoading(false);
        return;
      }

      // Build payload for UpdateReservations API
      // Use the displayed subtotal (sum over all rows) rather than only new items
      const displayedSubtotal = calculateSubtotal();
      const discountType = orderData?.discountType || 0;
      const discount = Number(orderData?.discount) || 0;
      const taxPercentage = Number(orderData?.taxPercentage) || 0;
      const taxAmount = Number(orderData?.taxAmount) || 0;
      const calculatedTotal = displayedSubtotal - discount + taxAmount; // retained for reference, not used

      const orderDetails = newServices.map((s) => {
        const nights =
          s.checkOutDate && s.checkInDate
            ? s.checkOutDate.diff(s.checkInDate, "days") || 1
            : 1;
        const parentIdVal = s.parentID || 0;
        return {
          // Send the same value for serviceID as parentID per requirement
          serviceID: parentIdVal,
          serviceTypeID: s.typeID || 0, // best available mapping
          typeID: s.typeID || 0,
          nightCount: nights,
          serviceQuantity: nights,
          defectedQuantity: 0,
          servicePrice: s.rate || 0,
          detailPrice: (s.rate || 0) * nights,
          parentID: parentIdVal,
          serviceDetailTotal: (s.rate || 0) * nights,
          brandID: 0,
          length: 0,
          breadth: 0,
          colorCode: 0,
          orderDetailPrimaryID: s.keyID || 0, // 0 for new rows
        };
      });

      const subCustomers = newServices.map((s) => ({
        customerID: s.subCustomerID || 0,
        companyID: companyID || 0,
        customerEmail: s.subCustomerEmail || "",
        customerName: s.subCustomerName || "",
        checkInDate: s.checkInDate
          ? s.checkInDate.toISOString()
          : dayjs().toISOString(),
        checkOutDate: s.checkOutDate
          ? s.checkOutDate.toISOString()
          : dayjs().add(1, "day").toISOString(),
      }));

      const payload = {
        orderID: orderData?.orderId || orderData?.orderID || 0,
        phoneNumber: orderData?.customerNumber || "",
        orderDate: orderData?.orderDate
          ? dayjs(orderData.orderDate).toISOString()
          : dayjs().toISOString(),
        checkOutDate: orderData?.checkOutDate
          ? dayjs(orderData.checkOutDate).toISOString()
          : dayjs().add(1, "day").toISOString(),
        subTotal: displayedSubtotal,
        discountType,
        discount,
        taxPercentage,
        taxAmount,

        total: displayedSubtotal,
        note: orderData?.notes || "",
        instruction: orderData?.instruction || "",
        status: orderData?.statusID || 0,
        paymentDate: (orderData?.paymentDate
          ? dayjs(orderData.paymentDate)
          : dayjs()
        ).toISOString(),
        receivedAmount: Number(orderData?.amountPaid) || 0,
        payAmount: Number(orderData?.payAmount) || 0,
        paymentType: orderData?.paymentType || 0,
        paymentNote: orderData?.paymentNote || "",
        orderDetails,
        subCustomers,
      };

      const res = await updateReservation(payload);
      if (
        res?.status >= 200 &&
        res?.status < 300 &&
        (res.status === 200 || res.status === 201)
      ) {
        message.success(res?.message || "Reservation updated successfully");
        setHasNewItems(false);
        // Optionally notify parent
        if (onSubmit) onSubmit(payload);
      } else {
        message.error(res?.message || "Failed to update reservation");
      }
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const subtotal = calculateSubtotal();

  return (
    <>
      <div
        style={{
          width: "100%",
          maxWidth: "100%",
          margin: "0",
          padding: "20px",
          background: "#fafbfc",
          borderRadius: companyID ? 0 : 12,
          boxShadow: companyID ? "none" : "0 4px 20px rgba(0,0,0,0.08)",
          maxHeight: "75vh",
          overflowY: "auto",
        }}
      >
        <Form form={form} layout="vertical">
          {companyID && (
            <>
              <div className="row">
                <div className="col-md-6">
                  <label
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Guest Name
                  </label>
                  <Input
                    value={templateGuest.guestName}
                    onChange={(e) =>
                      setTemplateGuest({
                        ...templateGuest,
                        guestName: e.target.value,
                      })
                    }
                    placeholder="Enter guest name"
                    style={{
                      borderRadius: 8,
                      border: "1.5px solid #e8f4f8",
                      background: "#fafbfc",
                      fontSize: 14,
                      padding: "10px 12px",
                    }}
                  />
                </div>
                <div className="col-md-6">
                  <label
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Guest Email
                  </label>
                  <Input
                    value={templateGuest.guestEmail}
                    onChange={(e) =>
                      setTemplateGuest({
                        ...templateGuest,
                        guestEmail: e.target.value,
                      })
                    }
                    placeholder="Enter guest email"
                    type="email"
                    style={{
                      borderRadius: 8,
                      border: "1.5px solid #e8f4f8",
                      background: "#fafbfc",
                      fontSize: 14,
                      padding: "10px 12px",
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <label
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Room Type
                  </label>
                  <Select
                    value={templateGuest.roomType || undefined} // ensure placeholder shows when undefined
                    onChange={(value) =>
                      setTemplateGuest({ ...templateGuest, roomType: value })
                    }
                    style={{ width: "100%", borderRadius: 8 }}
                    placeholder="Select room type"
                    allowClear
                  >
                    {loadingProducts && (
                      <Option value="loading" disabled>
                        Loading...
                      </Option>
                    )}
                    {!loadingProducts &&
                      !productsError &&
                      accommodationOptions.length > 0 &&
                      accommodationOptions.map((opt) => (
                        <Option key={opt.value} value={opt.label}>
                          {opt.label}
                        </Option>
                      ))}
                    {/* Removed static fallback room type options */}
                  </Select>
                </div>
                <div className="col-md-4">
                  <label
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Check In Date
                  </label>
                  <DatePicker
                    value={templateGuest.checkInDate}
                    onChange={(date) =>
                      handleTemplateDateChange("checkInDate", date)
                    }
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1.5px solid #e8f4f8",
                      background: "#fafbfc",
                    }}
                    format="YYYY-MM-DD"
                  />
                </div>
                <div className="col-md-4">
                  <label
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Check Out Date
                  </label>
                  <DatePicker
                    value={templateGuest.checkOutDate}
                    onChange={(date) =>
                      handleTemplateDateChange("checkOutDate", date)
                    }
                    style={{
                      width: "100%",
                      borderRadius: 8,
                      border: "1.5px solid #e8f4f8",
                      background: "#fafbfc",
                    }}
                    format="YYYY-MM-DD"
                  />
                </div>
              </div>

              <hr style={{ margin: "20px 0", border: "1px solid #e8f4f8" }} />
            </>
          )}

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6
                className="mb-0"
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Room/Service Details
              </h6>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddNewPerson}
                size="small"
                style={{
                  background:
                    "linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)",
                  border: "none",
                  borderRadius: 6,
                  fontWeight: 600,
                  boxShadow: "0 2px 8px rgba(212, 175, 55, 0.3)",
                }}
              >
                {companyID ? "Add Guest to Room" : "Add New Person"}
              </Button>
            </div>

            <Table
              columns={columns}
              dataSource={serviceDetails}
              pagination={false}
              scroll={{ x: companyID ? 1200 : 800 }}
              size="small"
              bordered
              style={{
                borderRadius: 8,
                overflow: "hidden",
                border: "1px solid #e8f4f8",
              }}
            />

            <div className="mt-3 text-end">
              <div className="d-inline-block">
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, #f8fafc 0%, #e8f4f8 100%)",
                    padding: "16px 20px",
                    borderRadius: 8,
                    border: "1px solid #e8f4f8",
                    minWidth: 280,
                  }}
                >
                  <div className="row fw-bold">
                    <div
                      className="col"
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#d4af37",
                      }}
                    >
                      Total:
                    </div>
                    <div
                      className="col-auto"
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#d4af37",
                      }}
                    >
                      R{" "}
                      {subtotal.toLocaleString("en-ZA", {
                        minimumFractionDigits: 2,
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Form>
      </div>

      {/* Fixed buttons outside scrollable container */}
      <div
        style={{
          padding: "20px",
          background: "#fff",
          borderTop: "1px solid #e8f4f8",
          position: "sticky",
          bottom: 0,
          zIndex: 10,
        }}
      >
        <div className="d-flex justify-content-end gap-2">
          <Button
            onClick={onCancel}
            style={{
              borderRadius: 6,
              border: "1.5px solid #043f6e",
              color: "#043f6e",
              fontWeight: 600,
              height: 36,
            }}
          >
            {hasNewItems ? "Cancel" : "Close"}
          </Button>
          {hasNewItems && (
            <Button
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              style={{
                background:
                  "linear-gradient(135deg, #043f6e 0%, #0a5a9bff 100%)",
                border: "none",
                borderRadius: 6,
                fontWeight: 600,
                height: 36,
                boxShadow: "0 2px 8px rgba(4, 63, 110, 0.3)",
              }}
            >
              Save Changes
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default EditQuotationForm;
